import React, { useState, useEffect } from 'react';
import { Text, StyleSheet } from 'react-native';
import { GlassCard } from '@/components/shared/redesign';
import { GlassFormInput } from '@/components/admin/redesign';
import { Button } from '@/components/common/admin/mobile/Button';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useUpdateResidentInfoMutation } from '@/store/api';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { IUpdateResidentInfoRequest } from '@/types/admin/residentDetails';
import { composeValidators, requiredString, emailFormat, phoneFormat, validateData } from '@/utils/admin/validation';

interface ResidentEditFormProps {
  accountId?: number;
  residentDetails: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
  };
}

export const ResidentEditForm: React.FC<ResidentEditFormProps> = ({
  accountId,
  residentDetails,
}) => {
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const [updateResidentInfo, { isLoading }] = useUpdateResidentInfoMutation();
  const styles = useStyles();

  // Form state
  const [formData, setFormData] = useState<IUpdateResidentInfoRequest>({
    leaseCustomerId: accountId,
    firstName: residentDetails.firstName || '',
    lastName: residentDetails.lastName || '',
    phonenumber: residentDetails.phoneNumber || '',
    email: residentDetails.email || '',
  });

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof IUpdateResidentInfoRequest, string>>>({});

  // Sync form state when residentDetails prop changes
  useEffect(() => {
    setFormData({
      leaseCustomerId: accountId,
      firstName: residentDetails.firstName || '',
      lastName: residentDetails.lastName || '',
      phonenumber: residentDetails.phoneNumber || '',
      email: residentDetails.email || '',
    });
    setErrors({});
  }, [residentDetails, accountId]);

  const validateForm = (): boolean => {
    const validator = composeValidators<IUpdateResidentInfoRequest>(
      requiredString('firstName', 'First name'),
      requiredString('lastName', 'Last name'),
      emailFormat('email'),
      phoneFormat('phonenumber'),
    );
    return validateData(formData, setErrors as any, validator);
  };

  const handleChange = (field: keyof IUpdateResidentInfoRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const result = await updateResidentInfo(formData).unwrap();
      if (result?.success) {
        showSuccess('User updated successfully');
      }
    } catch (error) {
      showApiError(error);
    }
  };

  const renderInput = (
    field: keyof IUpdateResidentInfoRequest,
    label: string,
    placeholder: string,
    keyboardType: 'default' | 'email-address' | 'phone-pad' = 'default',
  ) => (
    <GlassFormInput
      label={label}
      value={formData[field]?.toString() || ''}
      onChangeText={(value) => handleChange(field, value)}
      placeholder={placeholder}
      keyboardType={keyboardType}
      autoCapitalize={field === 'email' ? 'none' : 'words'}
      autoCorrect={false}
      error={errors[field]}
    />
  );

  return (
    <GlassCard style={styles.formCard}>
      <Text style={styles.title}>Edit Resident Information</Text>

      {renderInput('firstName', 'First Name', 'Enter first name')}
      {renderInput('lastName', 'Last Name', 'Enter last name')}
      {renderInput('phonenumber', 'Phone Number', 'Enter phone number', 'phone-pad')}
      {renderInput('email', 'Email', 'Enter email address', 'email-address')}

      <Button
        title="Save Updates"
        onPress={handleSubmit}
        loading={isLoading}
        disabled={isLoading}
        style={styles.submitButton}
      />
    </GlassCard>
  );
};

const useStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      formCard: {
        margin: 16,
        padding: 20,
      },
      title: {
        fontSize: 20,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 24,
      },
      submitButton: {
        marginTop: 8,
      },
    }),
  );
